import { Box, Button, Grid, Table } from '@flos/react-ui';
import type {
    CoverableChangesDTO,
    CoverableDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import CoverableChange from './CoverableChange';
import { jobAtom } from '../../../../../recoil/atoms/jobAtom';
import { useHippoText } from '../../../../../hooks/useHippoText';
import Currency from '../../../../../shared_components/Currency/Currency';
import { businessFlowSelector } from '../../../../../recoil/selectors/getBusinessFlowSelector';
import useWizardNavigation from '../../../../../hooks/useWizardNavigation';
import { currentCoverableIdAtom } from '../../../../../recoil/atoms/currentCoverableIdAtom';
import getCoverableAdjustDetailsConfig from '../../../../AdjustDetailsPage/config/adjustDetails';
import { IS_OPC_EDIT_AVAILABLE, OPC_EDIT_LOBS } from '../../../../OpcEntryPage/config/OpcConfig';
import { getCoverableChangeDateAsString } from '../../../../../utils/dateUtil';
import { getProductName } from '../../../../../utils/coverablesUtil';

const ShoppingBasketCoverableChangesList = ({
    coverableChanges,
    coverable
}: {
    coverableChanges: CoverableChangesDTO;
    coverable: CoverableDTO;
}): React.JSX.Element => {
    const { selectedPaymentFrequency } = useRecoilValue(jobAtom);
    const { wizardGotoStep } = useWizardNavigation();
    const businessFlow = useRecoilValue(businessFlowSelector);
    const setCurrentCoverableAtom = useSetRecoilState(currentCoverableIdAtom);
    const getHippoText = useHippoText();
    const hippoTexts = {
        priceChangeText: getHippoText(
            selectedPaymentFrequency === 'monthly'
                ? 'DSS-SHOPPING-BASKET-CHANGE-SUMMARY-MONTHLY-PRICE'
                : 'DSS-SHOPPING-BASKET-CHANGE-SUMMARY-YEARLY-PRICE'
        ),
        changeSummaryHeaderText: getHippoText('DSS-SHOPPING-BASKET-CHANGE-SUMMARY-HEADER'),
        adjustCoverableButtonText: getHippoText('DSS-SHOPPING-BASKET-ADJUST-COVERABLE'),
        changeSummaryBeforeText: getHippoText('DSS-SHOPPING-BASKET-CHANGE-SUMMARY-BEFORE'),
        changeSummaryForwardText: getHippoText('DSS-SHOPPING-BASKET-CHANGE-SUMMARY-FORWARD-DATE', {
            date_value: getCoverableChangeDateAsString(coverable.effectiveDate)
        })
    };

    const shouldShowAdjustButton = (): boolean => {
        const isCoverableEditable =
            getCoverableAdjustDetailsConfig(coverable, getHippoText, businessFlow, [
                coverableChanges
            ]).filter((property) => !property.hidden).length > 0;
        return (
            businessFlow !== 'ONSA' &&
            isCoverableEditable &&
            coverable.status === 'Added' &&
            IS_OPC_EDIT_AVAILABLE &&
            OPC_EDIT_LOBS.includes(getProductName(coverable.flavourName))
        );
    };

    const navigateToAdjustYourDetailsPage = (): void => {
        setCurrentCoverableAtom({
            fixedId: coverableChanges.coverableId,
            lobCode: coverableChanges.linePrefix
        });
        wizardGotoStep('AdjustDetailsPage');
    };

    return (
        <Box className="u-border-none u-shadow-none">
            <Grid variant="right-and-under">
                <div className="docs-grid-box">
                    <h4>{hippoTexts.changeSummaryHeaderText}</h4>
                </div>
                {shouldShowAdjustButton() && (
                    <div className="docs-grid-box u-flex-box u-flex-justify-end">
                        <Button onClick={navigateToAdjustYourDetailsPage}>
                            {hippoTexts.adjustCoverableButtonText}
                        </Button>
                    </div>
                )}
            </Grid>
            <Table theme="grey" wrapped>
                <Table.Body>
                    <Table.Row>
                        <Table.Cell>
                            <p className="u-spacing-stack-none paragraph--small strong">
                                {hippoTexts.changeSummaryBeforeText}
                            </p>
                        </Table.Cell>
                        <Table.Cell align="right">
                            <p className="u-spacing-stack-none paragraph--small strong">
                                {hippoTexts.changeSummaryForwardText}
                            </p>
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell>
                            {`${hippoTexts.priceChangeText} `}
                            <Currency
                                price={coverableChanges.priceBefore?.amount as number}
                                currencyString=""
                            />
                        </Table.Cell>
                        <Table.Cell align="right">
                            {`${hippoTexts.priceChangeText} `}
                            <Currency
                                price={coverableChanges.priceAfter?.amount as number}
                                currencyString=""
                            />
                        </Table.Cell>
                    </Table.Row>
                    {coverableChanges.changes.map((change) => (
                        <CoverableChange key={change.attribute} change={change} />
                    ))}
                </Table.Body>
            </Table>
        </Box>
    );
};

export default ShoppingBasketCoverableChangesList;
